/**
 * TemplateDetailView — `/templates/$templateId`
 *
 * Designer base de plantillas (B.6.d.2). Reemplaza el JSON editor de
 * B.5.b. Layout 3-col:
 *   - Izquierda: árbol estructural (StructureTree).
 *   - Centro: editor de la sección seleccionada (SectionEditorPane).
 *   - Derecha: preview + stats (SectionPreviewPane).
 *
 * Header con metadata del template + tabs internos (Designer activo;
 * User view / AI prompts / JSON / Versions / Used in disabled hasta
 * sus sub-bloques respectivos). Banner de configuración global
 * (name + description + idiomas).
 *
 * Modo:
 *   - **Edit** si el template es custom Y la versión actual está
 *     `published_at == null` (draft). Autosave debounced 1.5s tras
 *     último cambio.
 *   - **Read-only** si es system-default O la versión está publicada.
 *     Banner explicativo en top con acciones disabled (clonar / nueva
 *     versión / publicar / archivar) — entran en B.6.d.8.
 */

import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Loader2, Check, AlertCircle } from 'lucide-react'

import { ApiError, isForbiddenTier } from '../api/client'
import {
  getTemplate,
  getCurrentVersion,
  type ArtifactDefinition,
  type Template,
  type TemplateVersionWithDefinition,
} from '../api/templates'
import { useAccessToken } from '../auth/useAccessToken'
import { useMe } from '../auth/useMe'
import { editingLocale } from '../i18n/config'
import { tenantManageBlock, type TenantManageBlock } from '../lib/tiers'
import { BackButton } from '../components/shell/BackButton'
import { DesignerTabs, type DesignerTab } from '../components/templates/designer/DesignerTabs'
import { GlobalConfigBanner } from '../components/templates/designer/GlobalConfigBanner'
import { LifecycleDesignerView } from '../components/templates/designer/LifecycleDesignerView'
import { RoleDesignerView } from '../components/templates/designer/RoleDesignerView'
import { WorkflowDesignerView } from '../components/templates/designer/WorkflowDesignerView'
import { SectionEditorPane } from '../components/templates/designer/SectionEditorPane'
import { PromptsIaTab } from '../components/templates/designer/PromptsIaTab'
import { SectionPreviewPane } from '../components/templates/designer/SectionPreviewPane'
import { StructureTree } from '../components/templates/designer/StructureTree'
import { TemplateHeaderActions } from '../components/templates/designer/TemplateHeaderActions'
import { TemplateMethodologyControl } from '../components/templates/designer/TemplateMethodologyControl'
import { TemplateQualityControl } from '../components/templates/quality/TemplateQualityControl'
import { VersionsTab } from '../components/templates/designer/VersionsTab'
import { NoAccessView } from './NoAccessView'
import {
  findClientValidationIssues,
  useTemplateDesignerState,
} from '../components/templates/designer/state'
import {
  useDefinitionAutosave,
  type AutosaveState,
} from '../components/templates/designer/useDefinitionAutosave'

export function TemplateDetailView() {
  const { templateId } = useParams({ from: '/templates/$templateId' })
  const { accessToken } = useAccessToken()
  const { me } = useMe()
  // OT-0009 N1/N2 — `updateVersionDefinition` (`PATCH .../versions/{id}`)
  // exige admin en backend (`require_tenant_admin`, igual que
  // clonar/publicar/archivar en `TemplateHeaderActions`). Se calcula una
  // sola vez aquí y baja a `DesignerScreen`/`LifecycleDesignerView`/
  // `ReadOnlyBanner` — ninguno vuelve a llamar `useMe()`. Además exige que la
  // organización permita escribir (OT-0073 §4.4); `manageBlock` dice por qué no.
  const manageBlock = tenantManageBlock(me?.role, me?.tenant?.access)
  // `i18n.language` se incluye en el queryKey de `header` porque el
  // backend resuelve `name`/`description` por `Accept-Language` (Doc 19).
  // El `current` (definition completa) no depende del locale —
  // el bloque `i18n` interno trae ambos idiomas y el render elige.
  const { i18n: i18nInstance } = useTranslation()

  const templateQuery = useQuery({
    queryKey: ['templates', templateId, 'header', i18nInstance.language],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return getTemplate(accessToken, templateId)
    },
    enabled: !!accessToken,
    retry: false,
  })

  const versionQuery = useQuery({
    queryKey: ['templates', templateId, 'current'],
    queryFn: () => {
      if (!accessToken) throw new Error('no_access_token')
      return getCurrentVersion(accessToken, templateId)
    },
    enabled: !!accessToken,
    retry: false,
    staleTime: 30_000,
  })

  // Antes que la carga y que los datos en caché: si el backend negó la
  // lectura por tier, el diseñador (con su estado local y `PromptsIaTab`) no
  // se monta, o se desmonta si ya lo estaba.
  if (isForbiddenTier(templateQuery.error) || isForbiddenTier(versionQuery.error)) {
    return <NoAccessView />
  }
  if (templateQuery.isLoading || versionQuery.isLoading) {
    return <DesignerLoading />
  }
  if (templateQuery.error) return <DesignerError error={templateQuery.error} />
  if (versionQuery.error) return <DesignerError error={versionQuery.error} />
  if (!templateQuery.data || !versionQuery.data) return null

  // Dispatch por tipo de plantilla. Todos los editores comparten la
  // cabecera (metadata, metodología, estado del autosave, acciones) y el
  // banner de solo lectura; el cuerpo difiere por tipo.
  const tpl = templateQuery.data
  const ver = versionQuery.data
  const renderHeader = (autosave: AutosaveState) => (
    <DesignerHeader template={tpl} version={ver} autosave={autosave} />
  )
  const renderReadOnlyBanner = () => (
    <ReadOnlyBanner template={tpl} version={ver} manageBlock={manageBlock} />
  )
  if (tpl.template_type === 'lifecycle') {
    return (
      <LifecycleDesignerView
        template={tpl}
        version={ver}
        manageBlock={manageBlock}
        renderHeader={renderHeader}
        renderReadOnlyBanner={renderReadOnlyBanner}
      />
    )
  }
  if (tpl.template_type === 'role') {
    return (
      <RoleDesignerView
        template={tpl}
        version={ver}
        manageBlock={manageBlock}
        renderHeader={renderHeader}
        renderReadOnlyBanner={renderReadOnlyBanner}
      />
    )
  }
  if (tpl.template_type === 'approval_workflow') {
    return (
      <WorkflowDesignerView
        template={tpl}
        version={ver}
        manageBlock={manageBlock}
        renderHeader={renderHeader}
        renderReadOnlyBanner={renderReadOnlyBanner}
      />
    )
  }
  return <DesignerScreen template={tpl} version={ver} manageBlock={manageBlock} />
}

// ─── Designer principal (un nivel adentro para que useReducer arranque
//     después de que cargó la version) ──────────────────────────────────
// Exportado únicamente para el test de render de OT-0009 N1 — se prueba
// directo con `manageBlock` como prop, sin montar `TemplateDetailView`
// completo (que arrastraría router params + 2 queries).
export function DesignerScreen({
  template,
  version,
  manageBlock,
}: {
  template: Template
  version: TemplateVersionWithDefinition
  manageBlock: TenantManageBlock | null
}) {
  const { i18n } = useTranslation('templates')
  const [activeTab, setActiveTab] = useState<DesignerTab>('designer')

  // Read-only si:
  //  - es system-default (regla del modelo: customs solo, system se clona).
  //  - o la versión actual ya está publicada (inmutable).
  //  - o el usuario no puede administrar la organización (OT-0009 N1:
  //    `updateVersionDefinition` exige `require_tenant_admin`; antes cualquier
  //    tier podía editar y el autosave fallaba en silencio con 403) o ésta
  //    está en solo lectura (OT-0073).
  const readOnly =
    template.is_system_default || version.published_at !== null || manageBlock !== null

  const initialDefinition = useMemo<ArtifactDefinition>(
    () => version.definition as unknown as ArtifactDefinition,
    [version.definition],
  )

  const [state, dispatch] = useTemplateDesignerState(
    initialDefinition,
    editingLocale(Object.keys(initialDefinition.i18n), i18n.language),
  )

  // Si el version cambia (post-save remota o reload), re-hidratamos.
  useEffect(() => {
    dispatch({ type: 'SET_DEFINITION', definition: initialDefinition })
  }, [initialDefinition, dispatch])

  const autosave = useDefinitionAutosave({
    templateId: template.id,
    versionId: version.id,
    definition: state.definition,
    savedDefinition: initialDefinition,
    readOnly,
    preflight: findClientValidationIssues,
    onSaved: () => dispatch({ type: 'MARK_CLEAN' }),
  })

  return (
    <div className="h-full flex flex-col min-h-0">
      <DesignerHeader template={template} version={version} autosave={autosave} />
      <DesignerTabs active={activeTab} onChange={setActiveTab} />

      {readOnly ? (
        <ReadOnlyBanner template={template} version={version} manageBlock={manageBlock} />
      ) : null}

      <GlobalConfigBanner
        state={state}
        dispatch={dispatch}
        readOnly={readOnly}
      />

      {/* Layout cambia según el tab activo:
          - 'designer' → 2 cols (estructura más ancha + editor central).
          - 'user_view' → preview full-width con max-w centrado.
          Otros tabs (ai_prompts, json, versions, used_in) están disabled
          en DesignerTabs por ahora. */}
      {activeTab === 'designer' ? (
        <div className="flex-1 min-h-0 grid grid-cols-[360px_minmax(0,1fr)] divide-x divide-line">
          <StructureTree
            state={state}
            dispatch={dispatch}
            readOnly={readOnly}
          />
          <div className="min-w-0 overflow-hidden">
            <SectionEditorPane
              state={state}
              dispatch={dispatch}
              readOnly={readOnly}
              templateKey={template.template_key}
            />
          </div>
        </div>
      ) : activeTab === 'user_view' ? (
        <div className="flex-1 min-h-0 overflow-y-auto bg-canvas">
          <div className="max-w-3xl mx-auto py-6 px-4">
            <SectionPreviewPane state={state} />
          </div>
        </div>
      ) : activeTab === 'ai_prompts' ? (
        <PromptsIaTab state={state} />
      ) : activeTab === 'versions' ? (
        <div className="flex-1 min-h-0 overflow-y-auto bg-canvas">
          <div className="max-w-3xl mx-auto py-6 px-4">
            <VersionsTab template={template} />
          </div>
        </div>
      ) : null}
    </div>
  )
}

// ─── Header del template ─────────────────────────────────────────────────
function DesignerHeader({
  template,
  version,
  autosave,
}: {
  template: Template
  version: TemplateVersionWithDefinition
  autosave: AutosaveState
}) {
  const { t } = useTranslation('templates')
  const navigate = useNavigate()
  return (
    <header className="bg-surface border-b border-line px-6 py-3">
      <BackButton
        fallback={() => void navigate({ to: '/templates' })}
        className="inline-flex items-center gap-1.5 text-xs text-ink-muted hover:text-ink mb-2"
      >
        {t('designer.back_to_catalog')}
      </BackButton>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <span className="text-[10px] font-mono text-ink-subtle">
              {template.template_key}
            </span>
            <span className="text-[10px] font-mono text-ink-subtle">·</span>
            <span className="text-[10px] uppercase tracking-wider font-semibold text-ink-subtle">
              {t(`types.${template.template_type}` as 'types.artifact')}
            </span>
            <Tag>
              {template.is_system_default
                ? t('designer.header.tag_system')
                : t('designer.header.tag_custom')}
            </Tag>
            <Tag>
              {version.published_at
                ? t('designer.header.tag_published')
                : t('designer.header.tag_draft')}
            </Tag>
            <span className="text-[10px] font-mono text-ink-subtle">
              v{version.version}
            </span>
            <span className="text-[10px] font-mono text-ink-subtle">·</span>
            <TemplateMethodologyControl template={template} />
          </div>
          <h1 className="font-display text-xl font-semibold text-ink truncate">
            {template.name}
          </h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <TemplateQualityControl template={template} version={version} />
          <SaveStatusIndicator autosave={autosave} />
          <TemplateHeaderActions template={template} version={version} />
        </div>
      </div>
    </header>
  )
}

function Tag({ children }: { children: React.ReactNode }) {
  return (
    <span className="px-1.5 py-0.5 rounded bg-surface-muted text-[10px] font-mono uppercase tracking-wider text-ink-subtle">
      {children}
    </span>
  )
}

function SaveStatusIndicator({ autosave }: { autosave: AutosaveState }) {
  const { t } = useTranslation('templates')
  const { saveStatus: status, validationIssues, serverError } = autosave
  if (status === 'idle') return null
  if (status === 'saving' || status === 'pending') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-ink-subtle">
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        {t('designer.autosave.saving')}
      </span>
    )
  }
  if (status === 'saved') {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-official">
        <Check className="w-3.5 h-3.5" />
        {t('designer.autosave.saved')}
      </span>
    )
  }
  if (status === 'invalid') {
    const first = validationIssues[0]
    const detail = first
      ? t(`designer.autosave.invalid_${first.code}`, first.params)
      : t('designer.autosave.invalid_generic')
    const suffix =
      validationIssues.length > 1
        ? ` (+${validationIssues.length - 1})`
        : ''
    return (
      <span
        className="inline-flex items-center gap-1.5 text-xs text-critical max-w-md"
        title={detail}
      >
        <AlertCircle className="w-3.5 h-3.5 shrink-0" />
        <span className="truncate">
          {detail}
          {suffix}
        </span>
      </span>
    )
  }
  // status === 'error' — falla del PATCH (400/422/500). Si tenemos
  // detalle del backend (code + summary del primer Pydantic error),
  // mostrarlo inline + tooltip. Sin esto el user solo veía el genérico
  // "No se pudo guardar" sin saber qué parte del template estaba malformada.
  if (serverError) {
    const text = `${serverError.code}: ${serverError.summary}`
    return (
      <span
        className="inline-flex items-center gap-1.5 text-xs text-critical max-w-md"
        title={text}
      >
        <AlertCircle className="w-3.5 h-3.5 shrink-0" />
        <span className="truncate">{text}</span>
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-critical">
      <AlertCircle className="w-3.5 h-3.5" />
      {t('designer.autosave.error')}
    </span>
  )
}

// ─── Read-only banner ───────────────────────────────────────────────────
function ReadOnlyBanner({
  template,
  version,
  manageBlock,
}: {
  template: Template
  version: TemplateVersionWithDefinition
  manageBlock: TenantManageBlock | null
}) {
  const { t } = useTranslation('templates')
  const isSystem = template.is_system_default
  const isPublished = version.published_at !== null
  // OT-0009 N1 — motivo distinto de bloqueo: no repetir "es system" o
  // "está publicada" cuando la causa real es que el tier no alcanza.
  const message = isSystem
    ? t('designer.readonly.system_banner')
    : isPublished
      ? t('designer.readonly.published_banner')
      : manageBlock === 'not_admin'
        ? t('designer.readonly.not_admin_banner')
        : manageBlock === 'organization_read_only'
          ? t('designer.readonly.organization_read_only_banner')
          : ''
  return (
    <div className="bg-review-soft/40 border-b border-review/30 px-6 py-2.5 flex items-center gap-2 text-xs text-ink">
      <AlertCircle className="w-3.5 h-3.5 text-review shrink-0" />
      <span className="flex-1">{message}</span>
    </div>
  )
}

// ─── Loading + error gates ──────────────────────────────────────────────
function DesignerLoading() {
  const { t } = useTranslation('templates')
  return (
    <p className="font-mono text-xs text-ink-subtle">
      {t('designer.loading')}
    </p>
  )
}

function DesignerError({ error }: { error: unknown }) {
  const { t } = useTranslation(['templates', 'errors'])
  const code = error instanceof ApiError ? error.code : 'UNKNOWN'
  const fallback =
    error instanceof ApiError
      ? error.body?.message ?? error.message
      : error instanceof Error
        ? error.message
        : 'unknown'
  return (
    <div className="rounded-md border border-critical/40 bg-critical-soft p-4 font-mono text-xs text-critical">
      {t(`errors:${code}` as 'errors:UNKNOWN', { defaultValue: fallback })}
    </div>
  )
}
